import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { appendWorkingInterviews, createWorkingInterviews } from '~/actions/PermanentJobs';
import { Column, CustomSpinner, Text } from '~/commonComponents';
import { useAppSelector, useUserSelector, useGetOfficeById } from '~/hooks';
import { IUser, ProfessionTypeEnum } from '~/models';
import { Colors } from '~/themes/colors';
import { TWorkingInterviewFormData, WorkingInterviewForm } from '../components';
import { WorkingInterviewMutationMode } from '../utils';
import { useGetDraftJobPost } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';

type TLocationState = {
  mode: WorkingInterviewMutationMode;
};

export const WorkingInterviewPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation() as Partial<{ state: TLocationState }>;

  const { data: draftData, isLoading, isError, error } = useGetDraftJobPost(id);

  const { officeId, title, subProfession, professionType, officeSpecialty } = draftData || {};

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error, dispatch]);

  const currentUser: IUser = useUserSelector();
  const interviews = useAppSelector((state) => state.permanentJobs.workingInterviews);

  const unavailableDates = useMemo(() => {
    if (!id) return [];
    if (!interviews[id] || interviews[id]?.length === 0) return [];

    return interviews[id].map((it) => new Date(it.date));
  }, [id, interviews]);

  const formMode = useMemo(() => {
    const hasUnavailableDates = unavailableDates.length > 0;

    if (hasUnavailableDates) {
      return WorkingInterviewMutationMode.Append;
    }

    return state?.mode || WorkingInterviewMutationMode.Create;
  }, [state?.mode, unavailableDates.length]);

  const office = useGetOfficeById(officeId);
  const officeName = office?.office_name || '';

  const handleOnSubmit = (formData: TWorkingInterviewFormData) => {
    if (!id) return;

    const payload = {
      breakTime: formData.breakTime,
      dates: formData.dates,
      hourlyRate: formData.hourlyRate,
      hoursArrival: formData.hoursArrival,
      hoursDeparture: formData.hoursDeparture,
      isAssistedHygiene: formData.isAssistedHygiene,
      isPaidBreakTime: formData.isPaidBreakTime,
      specialty: formData.specialty,
    };

    if (formMode === WorkingInterviewMutationMode.Append) {
      dispatch(appendWorkingInterviews(id, payload));
    } else {
      dispatch(createWorkingInterviews(id, payload));
    }

    navigate(`/permanent-jobs/postings/${id}/review-and-payment`);
  };

  if (isLoading) {
    return <CustomSpinner isLoading />;
  }

  return (
    <Column marginInline="auto" width={680}>
      <Column marginBottom={16} gap={8}>
        <Text bold color={Colors.primary_500} fontSize={20}>
          Schedule Working Interviews
        </Text>
        <Text color={Colors.neutral_500} italic>
          Add working interviews as part of your process. They’re designed to help you evaluate a
          candidate’s skills and team compatibility before hiring.
        </Text>
      </Column>
      <WorkingInterviewForm
        defaultValues={{
          breakTime: null,
          dates: [],
          hourlyRate: '',
          hoursArrival: '',
          hoursDeparture: '',
          isAssistedHygiene: currentUser.office_info.isAssistedHygiene,
          isPaidBreakTime: false,
          specialty: officeSpecialty ? `specialty_${officeSpecialty}` : '',
        }}
        jobTitle={title || ''}
        office={officeName}
        profession={subProfession || professionType || ProfessionTypeEnum.RDH}
        unavailableDates={unavailableDates}
        onSubmit={handleOnSubmit}
      />
    </Column>
  );
};
